/**
 * CEFR proficiency scale — the cognitive/linguistic difficulty axis.
 *
 * Cards and decks can pin a level; the dashboard and filters use it to surface
 * level-appropriate material.
 */

import type { Localized } from "./locale";

export type CefrLevelId = "A1" | "A2" | "B1" | "B2" | "C1" | "C2";

export type CefrLevelRef = {
  id: CefrLevelId;
  name: Localized;
  /** One-line goal description. */
  summary: Localized;
  /** Difficulty 0..1 for sorting / weighting in adaptive flows. */
  difficulty: number;
};

export const CEFR_LEVELS: CefrLevelRef[] = [
  {
    id: "A1",
    name: {
      ru: "A1 · Начинающий",   en: "A1 · Beginner",          es: "A1 · Principiante", fr: "A1 · Débutant",
      de: "A1 · Anfänger",     it: "A1 · Principiante",      zh: "A1 · 入门",          ja: "A1 · 初級",
    },
    summary: {
      ru: "Базовые слова и приветствия", en: "Basic words and greetings", es: "Palabras básicas y saludos",
      fr: "Mots et salutations de base", de: "Grundwörter und Begrüßungen", it: "Parole base e saluti",
      zh: "基础词汇与问候",                ja: "基本語と挨拶",
    },
    difficulty: 0.1,
  },
  {
    id: "A2",
    name: {
      ru: "A2 · Элементарный", en: "A2 · Elementary",        es: "A2 · Elemental",    fr: "A2 · Élémentaire",
      de: "A2 · Grundstufe",   it: "A2 · Elementare",        zh: "A2 · 初阶",          ja: "A2 · 初級+",
    },
    summary: {
      ru: "Простые фразы и быт", en: "Simple phrases and everyday life", es: "Frases simples y vida diaria",
      fr: "Phrases simples et vie quotidienne", de: "Einfache Sätze und Alltag", it: "Frasi semplici e vita quotidiana",
      zh: "简单短语与日常",       ja: "簡単なフレーズと日常",
    },
    difficulty: 0.25,
  },
  {
    id: "B1",
    name: {
      ru: "B1 · Разговорный", en: "B1 · Intermediate",       es: "B1 · Intermedio",   fr: "B1 · Intermédiaire",
      de: "B1 · Mittelstufe", it: "B1 · Intermedio",         zh: "B1 · 中级",          ja: "B1 · 中級",
    },
    summary: {
      ru: "Уверенный разговор на знакомые темы",
      en: "Confident conversation on familiar topics",
      es: "Conversación fluida sobre temas conocidos",
      fr: "Conversation à l'aise sur des sujets familiers",
      de: "Sicheres Gespräch über vertraute Themen",
      it: "Conversazione sicura su argomenti familiari",
      zh: "对熟悉话题的流利对话",
      ja: "馴染みのある話題で自信を持って会話",
    },
    difficulty: 0.45,
  },
  {
    id: "B2",
    name: {
      ru: "B2 · Средний+",       en: "B2 · Upper-Intermediate", es: "B2 · Intermedio Alto", fr: "B2 · Avancé",
      de: "B2 · Fortgeschritten",it: "B2 · Intermedio-Alto",    zh: "B2 · 中高级",          ja: "B2 · 中上級",
    },
    summary: {
      ru: "Свободное общение и аргументация",
      en: "Fluent communication and argumentation",
      es: "Comunicación fluida y argumentación",
      fr: "Communication fluide et argumentation",
      de: "Fließende Kommunikation und Argumentation",
      it: "Comunicazione fluente e argomentazione",
      zh: "流畅交流与论证",
      ja: "流暢なコミュニケーションと議論",
    },
    difficulty: 0.65,
  },
  {
    id: "C1",
    name: {
      ru: "C1 · Профессиональный", en: "C1 · Advanced",        es: "C1 · Avanzado",   fr: "C1 · Autonome",
      de: "C1 · Sehr fortgeschritten", it: "C1 · Avanzato",    zh: "C1 · 高级",        ja: "C1 · 上級",
    },
    summary: {
      ru: "Сложные тексты и проф. контекст",
      en: "Complex texts and professional context",
      es: "Textos complejos y contexto profesional",
      fr: "Textes complexes et contexte professionnel",
      de: "Komplexe Texte und beruflicher Kontext",
      it: "Testi complessi e contesto professionale",
      zh: "复杂文本与专业语境",
      ja: "複雑なテキストと専門的文脈",
    },
    difficulty: 0.85,
  },
  {
    id: "C2",
    name: {
      ru: "C2 · Носитель", en: "C2 · Proficient", es: "C2 · Maestría", fr: "C2 · Maîtrise",
      de: "C2 · Muttersprachlich", it: "C2 · Padronanza", zh: "C2 · 精通", ja: "C2 · 熟達",
    },
    summary: {
      ru: "Академический и литературный язык",
      en: "Academic and literary language",
      es: "Lenguaje académico y literario",
      fr: "Langue académique et littéraire",
      de: "Akademische und literarische Sprache",
      it: "Linguaggio accademico e letterario",
      zh: "学术与文学语言",
      ja: "学術・文学レベルの言語",
    },
    difficulty: 1.0,
  },
];

export const CEFR_BY_ID: Record<CefrLevelId, CefrLevelRef> = Object.fromEntries(
  CEFR_LEVELS.map((l) => [l.id, l]),
) as Record<CefrLevelId, CefrLevelRef>;

export function isCefrLevel(value: unknown): value is CefrLevelId {
  return typeof value === "string" && value in CEFR_BY_ID;
}
